import React, { useEffect, useState, useRef } from 'react';
import Tooltip from '@mui/material/Tooltip';
import Icon from '@mui/material/Icon';
import MDBox from 'components/MDBox';
import MDBadgeDot from 'components/MDBadgeDot';
import MDButton from 'components/MDButton';
import Card from '@mui/material/Card';
import { LineChart } from '@mui/x-charts/LineChart';
import MDTypography from 'components/MDTypography';
import { useResizeObserver } from '@mui/x-charts/hooks';

function MonthlyProjectDistribution({ projects }) {
    const [chartData, setChartData] = useState(null);
    const [chartWidth, setChartWidth] = useState(500);
    const chartContainerRef = useRef(null);
    const currentYear = new Date().getFullYear();
    const prevYear = currentYear - 1;
    const twoYearsAgo = currentYear - 2;

    // Resize observer to get container width
    useEffect(() => {
        if (chartContainerRef.current) {
            const resizeObserver = new ResizeObserver((entries) => {
                if (entries[0]) {
                    setChartWidth(entries[0].contentRect.width);
                }
            });

            resizeObserver.observe(chartContainerRef.current);

            return () => {
                resizeObserver.disconnect();
            };
        }
    }, []);

    useEffect(() => {
        console.log('Projects data:', JSON.stringify(projects));

        if (!projects || !Array.isArray(projects) || projects.length === 0) {
            console.log('No projects data available');
            setChartData(null);
            return;
        }

        // Test verileri
        const testData = [
            { value: 10 },
            { value: 15 },
            { value: 8 },
            { value: 12 },
            { value: 20 },
            { value: 18 },
            { value: 25 },
            { value: 30 },
            { value: 28 },
            { value: 22 },
            { value: 17 },
            { value: 14 },
        ];

        try {
            // Ay isimleri
            const monthNames = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];

            // Gerçek veri işleme
            const currentYearData = Array(12).fill(0);
            const prevYearData = Array(12).fill(0);
            const twoYearsAgoData = Array(12).fill(0);

            projects.forEach((project) => {
                if (project.createdAt) {
                    try {
                        const date = new Date(project.createdAt);
                        const year = date.getFullYear();
                        const month = date.getMonth();

                        if (year === currentYear) {
                            currentYearData[month]++;
                        } else if (year === prevYear) {
                            prevYearData[month]++;
                        } else if (year === twoYearsAgo) {
                            twoYearsAgoData[month]++;
                        }
                    } catch (error) {
                        console.error('Error processing date:', project.createdAt, error);
                    }
                }
            });

            console.log('Processed data:', {
                monthNames,
                currentYearData,
                prevYearData,
                twoYearsAgoData,
            });

            // Demoyu gösterdiğimizden emin olmak için en azından bir veri noktasını 1 yapalım
            if (
                !currentYearData.some((v) => v > 0) &&
                !prevYearData.some((v) => v > 0) &&
                !twoYearsAgoData.some((v) => v > 0)
            ) {
                console.log('No data points, using test data');
                setChartData({
                    labels: monthNames,
                    series: [
                        {
                            data: testData.map((item) => item.value),
                            label: 'Test Verileri',
                            color: '#4CAF50',
                        },
                    ],
                });
            } else {
                setChartData({
                    labels: monthNames,
                    series: [
                        {
                            data: currentYearData,
                            label: `${currentYear} Projeleri`,
                            color: '#4CAF50',
                        },
                        {
                            data: prevYearData,
                            label: `${prevYear} Projeleri`,
                            color: '#1A73E8',
                        },
                        {
                            data: twoYearsAgoData,
                            label: `${twoYearsAgo} Projeleri`,
                            color: '#F44335',
                        },
                    ],
                });
            }
        } catch (error) {
            console.error('Error processing chart data:', error);
            // Hata durumunda test verileri kullan
            setChartData({
                labels: ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'],
                series: [
                    {
                        data: testData.map((item) => item.value),
                        label: 'Test Verileri',
                        color: '#4CAF50',
                    },
                ],
            });
        }
    }, [projects, currentYear, prevYear, twoYearsAgo]);

    return (
        <Card sx={{ height: '100%' }}>
            <MDBox height="20rem" mt={3} ref={chartContainerRef}>
                {chartData ? (
                    <LineChart
                        xAxis={[
                            {
                                data: chartData.labels,
                                scaleType: 'point',
                            },
                        ]}
                        series={chartData.series}
                        width={chartWidth}
                        height={320}
                        margin={{ left: 20, right: 30, top: 30, bottom: 60 }}
                        sx={{
                            '.MuiLineElement-root': {
                                strokeWidth: 2,
                            },
                            '.MuiMarkElement-root': {
                                stroke: 'white',
                                strokeWidth: 2,
                                r: 5,
                                fill: 'currentColor',
                            },
                        }}
                    />
                ) : (
                    <MDBox display="flex" alignItems="center" justifyContent="center" height="100%">
                        <MDTypography variant="button" color="text">
                            Görüntülenecek veri bulunamadı
                        </MDTypography>
                    </MDBox>
                )}
            </MDBox>
        </Card>
    );
}

export default MonthlyProjectDistribution;
